/**
 * TanStack Query-хуки для каждого API-эндпоинта.
 *
 * Логика кеширования:
 * - me, subscription — fresh 30s, после этого refetch при focus/reconnect
 * - categories, tariffs — fresh 5 минут (редко меняются)
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./client";
import { isActiveSubscription } from "./types";
import type {
  AdminDashboardOut,
  AdminFunnelOut,
  AdminPromoItem,
  AdminPromoListOut,
  AdminRange,
  AdminStatsOut,
  AdminUserProfileOut,
  RegularRemovalOut,
  UserJournalOut,
  AdminUserSearchOut,
  BroadcastListOut,
  BroadcastOut,
  CategoriesListOut,
  KBArticleCategoryOut,
  KBCountsOut,
  KBLessonCategoryOut,
  KBLessonsListOut,
  KBSectionsResponse,
  LessonsListOut,
  VoteLessonOut,
  MeOut,
  ProfileOut,
  ProfileUpdateIn,
  QuestsListOut,
  ReferralsOut,
  SetCategoriesIn,
  SubscriptionCheckOut,
  SubscriptionMeResponse,
  SubscriptionQuestsOut,
  TariffsListOut,
} from "./types";


export const queryKeys = {
  me: ["me"] as const,
  categories: ["categories"] as const,
  subscriptionMe: ["subscription", "me"] as const,
  autocharge: ["subscription", "autocharge"] as const,
  tariffs: ["subscription", "tariffs"] as const,
  profile: ["profile", "me"] as const,
  referrals: ["referrals", "me"] as const,
  quests: ["quests", "me"] as const,
  subscriptionQuests: ["quests", "subscriptions"] as const,
  retentionOffer: ["subscription", "retention-offer"] as const,
  giftWindow: ["subscription", "gift"] as const,
};

export function useMe() {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: () => apiFetch<MeOut>("/me"),
    staleTime: 30_000,
  });
}

/**
 * Free-тариф: юзер натиснув «Отримувати вакансії» у попапі-опитуванні.
 * Ставить флаг згоди на бекенді (idempotent). Оптимістично проставляємо
 * me.free_optin=true → попап більше не показується. Owner 2026-07-09.
 */
export function useFreeOptIn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ ok: boolean; free_optin: boolean }>("/free/opt-in", {
        method: "POST",
      }),
    onSuccess: () => {
      const prev = qc.getQueryData<MeOut>(queryKeys.me);
      if (prev) qc.setQueryData<MeOut>(queryKeys.me, { ...prev, free_optin: true });
      qc.invalidateQueries({ queryKey: queryKeys.me });
    },
  });
}

/**
 * Позначити інструкцію-підказку пройденою ('basic' або ключ розділу).
 * Idempotent. Оптимістично додаємо ключ у me.seen_hints → оверлей/підказка
 * зникає одразу, потім інвалідуємо /me для звірки з сервером.
 */
export function useMarkHintSeen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (hint: string) =>
      apiFetch<{ ok: boolean; seen_hints: string[] }>("/instruction/seen", {
        method: "POST",
        body: { hint },
      }),
    onMutate: (hint: string) => {
      const prev = qc.getQueryData<MeOut>(queryKeys.me);
      if (prev && !prev.seen_hints.includes(hint)) {
        qc.setQueryData<MeOut>(queryKeys.me, {
          ...prev,
          seen_hints: [...prev.seen_hints, hint],
        });
      }
    },
    onSuccess: (data) => {
      const prev = qc.getQueryData<MeOut>(queryKeys.me);
      if (prev) qc.setQueryData<MeOut>(queryKeys.me, { ...prev, seen_hints: data.seen_hints });
      qc.invalidateQueries({ queryKey: queryKeys.me });
    },
  });
}

export function useCategories() {
  return useQuery({
    queryKey: queryKeys.categories,
    queryFn: () => apiFetch<CategoriesListOut>("/categories"),
    staleTime: 5 * 60_000,
  });
}

export function useSetCategories() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SetCategoriesIn) =>
      apiFetch<CategoriesListOut>("/categories", { method: "PUT", body: payload }),
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.categories, data);
    },
  });
}

export function useMySubscription() {
  return useQuery({
    queryKey: queryKeys.subscriptionMe,
    queryFn: () => apiFetch<SubscriptionMeResponse>("/subscription/me"),
    // staleTime=0 + refetch завжди — \u044e\u0437\u0435\u0440 \u0441\u043a\u0430\u0440\u0436\u0438\u0432\u0441\u044f \u043d\u0430 \u0441\u0442\u0430\u0440\u0438\u0439 freeze
    // \u0431\u0430\u043b\u0430\u043d\u0441 \u0432 UI \u043f\u0456\u0441\u043b\u044f admin grant. \u041a\u0440\u0430\u0449\u0435 \u043b\u0438\u0448\u043d\u0456\u0439 fetch \u043d\u0456\u0436 \u0441\u0442\u0430\u043b\u0438\u0439
    // \u0434\u0430\u043d\u043d\u0438\u0439 (2026-05-22).
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
    refetchOnReconnect: "always",
  });
}

export interface CancelRecurringOut {
  success: boolean;
  is_recurring: boolean;
  message: string;
  /** Спецпропозиція, яку сервер запустив ПІСЛЯ вимкнення (owner 2026-10-22).
   *  Немає або available=false — цього разу офера немає. */
  retention_offer?: RetentionOfferOut | null;
}

export function useCancelRecurring() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<CancelRecurringOut>("/subscription/cancel-recurring", { method: "POST" }),
    onSuccess: (data) => {
      // Освежаем состояние подписки
      qc.invalidateQueries({ queryKey: queryKeys.subscriptionMe });
      qc.invalidateQueries({ queryKey: queryKeys.autocharge });
      // Офер, запущений після вимкнення, — одразу в кеш: попап і банер
      // показують його без зайвого запиту.
      if (data.retention_offer) {
        qc.setQueryData(queryKeys.retentionOffer, data.retention_offer);
      }
    },
    onError: () => {
      // Збій — теж перезапитуємо стан: частину (скажімо, monobank) могли
      // вже вимкнути, і рядок «Автопродовження» має показати правду.
      qc.invalidateQueries({ queryKey: queryKeys.subscriptionMe });
      qc.invalidateQueries({ queryKey: queryKeys.autocharge });
    },
  });
}

/** Живе автосписання без діючої підписки (owner 2026-10-19). */
export interface AutochargeOut {
  live: boolean;
  has_subscription: boolean;
  provider?: "mono" | "wfp" | null;
  /** WayForPay не відповів — стан невідомий. */
  unknown?: boolean;
  /** ДД.ММ.РРРР — наступна спроба списання (якщо відома). */
  next_charge?: string | null;
  amount_uah?: string | null;
}

/**
 * Чи можуть ще списатись гроші, коли підписки вже немає (скінчилась, тест
 * добіг кінця, ідуть повтори списання). Питаємо лише тоді, коли сторінка
 * показує «немає підписки» — сервер кешує відповідь WayForPay.
 */
export function useAutocharge(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.autocharge,
    queryFn: () => apiFetch<AutochargeOut>("/subscription/autocharge"),
    enabled,
    staleTime: 60_000,
  });
}

export function useTariffs() {
  return useQuery({
    queryKey: queryKeys.tariffs,
    queryFn: () => apiFetch<TariffsListOut>("/subscription/tariffs"),
    staleTime: 5 * 60_000,
  });
}

// ============================================================
// Retention offer — одноразовий офер 449₴ при першому вимкненні автопродовження
// ============================================================

export interface RetentionOfferOut {
  available: boolean;
  expires_at: string | null;
  price_uah: number;
  original_price_uah: number;
  discount_percent: number;
  /** 1 — «два місяці за ціною одного», 2 — місяць зі знижкою (owner 2026-07-29). */
  step: number;
  two_months_price_uah: number;
  two_months_days: number;
}

/** Подарунок маркетинг-машини: живе вікно 72 год (owner 2026-08-11).
 *  Потрібен і для плашки з таймером, і для позначок «+N днів» на тарифах —
 *  людина приходить у тарифи вже без повідомлення перед очима. */
export interface GiftOfferOut {
  offer: string;
  kind: "days" | "discount" | "trial";
  title: string;
  body: string;
  button: string;
  bonus_days: number;
  min_tariff_days: number;
  max_tariff_days?: number | null;
  tariff_slug: string | null;
  /** Ціни рахує сервер із тарифу офера (owner 2026-09-04). */
  price_uah?: number;
  old_price_uah?: number;
  discount_percent?: number;
  expires_at: string;
}

export interface GiftWindowOut {
  active: boolean;
  /** Подарунків може бути кілька одночасно (owner 2026-08-11). */
  offers?: GiftOfferOut[];
  offer?: string;
  title?: string;
  body?: string;
  button?: string;
  /** days — подарунок днями, discount — 449 замість 990, trial — 14 днів за 1 ₴. */
  kind?: "days" | "discount" | "trial";
  bonus_days?: number;
  min_tariff_days?: number;
  max_tariff_days?: number | null;
  tariff_slug?: string | null;
  price_uah?: number;
  old_price_uah?: number;
  discount_percent?: number;
  expires_at?: string;
}

/** Рахунок за оффером з окремою ціною (449 ₴ / 14 днів за 1 ₴).
 *  Гейт на сервері — живе вікно подарунка, тому посилання нікому не переслати. */
export function useGiftInvoice() {
  return useMutation({
    mutationFn: (offer?: string) =>
      apiFetch<{ order_reference: string; purchase_url: string }>(
        "/subscription/gift/invoice",
        { method: "POST", body: { offer: offer ?? null } },
      ),
  });
}

export function useGiftWindow() {
  return useQuery({
    queryKey: queryKeys.giftWindow,
    queryFn: () => apiFetch<GiftWindowOut>("/subscription/gift"),
    staleTime: 30_000,
    refetchOnWindowFocus: "always",
  });
}

/** Стан офера. enabled=false → не робимо запит (для юзерів без активної
 *  підписки офер неможливий). Кеш дедупиться по queryKey для всіх спожив. */
export function useRetentionOffer(enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.retentionOffer,
    queryFn: () =>
      apiFetch<RetentionOfferOut>("/subscription/retention-offer"),
    enabled,
    staleTime: 0,
    refetchOnWindowFocus: "always",
  });
}

/** Гейтований варіант: тягне офер лише коли є активна підписка. */
export function useRetentionOfferGated() {
  const sub = useMySubscription();
  const active = !!(sub.data && isActiveSubscription(sub.data));
  return useRetentionOffer(active);
}

/** Стан колеса: чи є оберт, коли наступний, топ перемог місяця. */
export interface WheelStateOut {
  available: boolean;
  /** Скільки невикористаних обертів на руках (owner 2026-09-24). */
  spins_left?: number;
  /** Із них безкоштовних місячних (0 або 1). */
  free_left?: number;
  /** Свіжа оплачена покупка обертів, яку людина ще не бачила — для попапа
   *  «Оберти вже твої» (owner 2026-09-24). */
  purchase?: { order_reference: string; spins: number } | null;
  /** Останній зіграний приз — для кнопки «Забрати приз» із листа-вибачення
   *  і для попапа призу, якого людина не побачила (05.10: колесо не
   *  зупинилось, застосунок закрили). `spun_at` і `id` — щоб упізнати оберт. */
  last_prize?: {
    prize_key: string;
    amount: number;
    spun_at?: string | null;
    id?: number | null;
  } | null;
  next_grant_at: string;
  /** Розпочате й не завершене подвоєння (owner 2026-10-25) — відкрити вікно знову після перезапуску (людина ходила в Instagram). */
  double_pending?: (WheelDoubleState & { spin_id: number }) | null;
  /** Топ перемог місяця (owner 2026-10-11): до 10 найцінніших виграшів за
   *  останні 30 діб, від найкращого до найгіршого. «Ще оберт» сюди не йде.
   *  `avatar_url` — підписане посилання на фото з Telegram (null — фото
   *  немає); `username` — лише в тих, у кого він є: за ним відкриваємо профіль. */
  top_month?: {
    name: string;
    prize_key: string;
    amount: number;
    at: string | null;
    username?: string | null;
    avatar_url?: string | null;
  }[];
  /** Пакети платних обертів і скільки їх ще можна купити цього місяця. */
  packs?: { slug: string; spins: number; amount: number; title: string }[];
}

export function useWheelState(opts?: {
  enabled?: boolean;
  refetchInterval?: number | false;
}) {
  return useQuery({
    queryKey: ["wheel"] as const,
    queryFn: () => apiFetch<WheelStateOut>("/wheel"),
    staleTime: 30_000,
    enabled: opts?.enabled ?? true,
    refetchInterval: opts?.refetchInterval ?? false,
  });
}

/** Подвоєння призу (owner 2026-10-25; з 04.11.2026 — за підписку на Instagram inFLOW). */
export interface WheelDoubleState {
  eligible: boolean;
  /** pending — заявку прийнято, чекає перевірки підписки. */
  status?: "open" | "pending" | "doubled" | "expired" | "declined" | "month_used";
  /** Людина вже відкривала подвоєння (застосунок перезапустився, поки вона в Instagram). */
  started?: boolean;
  /** Повторна спроба після відмови (лист «Спробувати ще раз»). */
  retry?: boolean;
  prize_key?: string;
  amount?: number;
  doubled_amount?: number;
  /** «3 дні підписки» */
  prize?: string;
  /** «6 днів підписки» */
  doubled?: string;
  extra?: number;
  /** На кого підписатись: @inflow.in.ua. */
  brand_handle?: string;
  brand_url?: string;
  /** Сторінка людини, яку вона вже надсилала, — підставляємо в поле. */
  ig_url?: string | null;
}

export function useWheelDouble(spinId: number | null) {
  return useQuery({
    queryKey: ["wheel", "double", spinId],
    queryFn: () => apiFetch<WheelDoubleState>(`/wheel/double/${spinId}`),
    enabled: spinId !== null,
    staleTime: 15_000,
  });
}

/** Стан подвоєння беремо одразу, як сервер назвав приз (колесо ще крутиться), — щоб попап виграшу
 *  з'явився вже з обома кнопками, а не «доростав» другою (owner 2026-10-26). */
export function prefetchWheelDouble(qc: ReturnType<typeof useQueryClient>, spinId: number): void {
  void qc.prefetchQuery({
    queryKey: ["wheel", "double", spinId],
    queryFn: () => apiFetch<WheelDoubleState>(`/wheel/double/${spinId}`),
    staleTime: 15_000,
  });
}

/** Сторінка в Instagram → заявка на подвоєння. Відповідь — новий стан (`pending`). */
export function useSubmitWheelDouble(spinId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (url: string) =>
      apiFetch<WheelDoubleState>(`/wheel/double/${spinId}`, {
        method: "POST",
        body: { url },
        timeoutMs: 30_000,
      }),
    onSuccess: (data) => {
      qc.setQueryData(["wheel", "double", spinId], data);
      qc.invalidateQueries({ queryKey: ["wheel"] });
    },
  });
}

/** «started» — відкрив подвоєння; «declined» — підтвердив «Пропустити» (owner 2026-10-25: лише в моменті);
 *  «insta» — натиснув «Відкрити Instagram» (лише для воронки в адмінці). */
export function markWheelDouble(spinId: number, what: "started" | "declined" | "insta") {
  return apiFetch<WheelDoubleState>(`/wheel/double/${spinId}/mark/${what}`, { method: "POST" });
}

/** Оберт. Приз обирає СЕРВЕР — клієнт лише доводить анімацію до сектора. */
export interface WheelSpinOut {
  sector: number;
  prize_key: string;
  kind: "bonus" | "days" | "promo" | "spin";
  amount: number;
  next_grant_at: string;
  /** Номер і час оберту — ті самі, що прийдуть у `last_prize` (05.10). */
  spin_id?: number;
  spun_at?: string;
}

/** Рахунок на пакет обертів. Сервер сам перевіряє місячний ліміт. */
/** Попап після покупки показано — сервер більше не віддає цю покупку. */
export async function markWheelPurchaseSeen(orderReference: string): Promise<void> {
  await apiFetch("/wheel/purchase/seen", {
    method: "POST",
    body: { order_reference: orderReference },
  });
}

export function useWheelPurchase() {
  return useMutation({
    mutationFn: (pack: string) =>
      apiFetch<{ url: string; order_reference: string }>("/wheel/purchase", {
        method: "POST",
        // apiFetch сам серіалізує body — рядок тут давав подвійне кодування і 422.
        body: { pack },
      }),
  });
}

export function useWheelSpin() {
  return useMutation({
    mutationFn: () => apiFetch<WheelSpinOut>("/wheel/spin", { method: "POST" }),
    // Стан колеса й підписки оновлює СТОРІНКА, коли колесо зупинилось і приз
    // уже показано. Інакше список переможців показував приз ще під час
    // обертання (owner 2026-09-23).
  });
}

/** Оновити стан колеса й підписки — після того, як приз показано. */
export function useRefreshWheel() {
  const qc = useQueryClient();
  // Проміс — щоб сторінка колеса могла дочекатись свіжого стану і впізнати
  // приз, якщо відповідь на оберт загубилась (05.10).
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ["wheel"] }),
      qc.invalidateQueries({ queryKey: queryKeys.subscriptionMe }),
      // Лічильник обертів їде і в профілі — оновлюємо, щоб головна не
      // показала старе число, поки /wheel перезавантажується.
      qc.invalidateQueries({ queryKey: queryKeys.me }),
    ]).then(() => undefined);
}

/** Людина дійшла до оффера в застосунку (owner 2026-09-01).
 *  Без цієї мітки зламану кнопку не видно: доставка зелена, а попап не
 *  відкривається — так `?gift=1` мовчав із 28.08. */
export function useMarkGiftOpened() {
  return useMutation({
    mutationFn: () =>
      apiFetch<{ marked: number }>("/subscription/gift/opened", {
        method: "POST",
      }),
  });
}

/** Оформити офер → WFP-інвойс (449₴ → рекуррент 990/міс). */
/** Відмова від поточного кроку офера → сервер віддає наступний крок. */
export function useDeclineRetentionStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<RetentionOfferOut>("/subscription/retention-offer/decline", {
        method: "POST",
      }),
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.retentionOffer, data);
      qc.invalidateQueries({ queryKey: queryKeys.retentionOffer });
    },
  });
}

export function useRetentionOfferInvoice() {
  return useMutation({
    mutationFn: () =>
      apiFetch<{ order_reference: string; purchase_url: string }>(
        "/subscription/retention-offer/invoice",
        { method: "POST" },
      ),
  });
}

// ============================================================
// Subscription purchase / freeze / promo (2026-05-20)
// ============================================================

export interface CreateInvoiceOut {
  order_reference: string;
  purchase_url: string;
  fields: Record<string, string | string[]>;
  bonus_discount_uah?: number;
  final_amount_uah?: number;
}

export interface CreateInvoiceArgs {
  tariff_slug: string;
  use_bonus?: boolean;
  /** Ключ офера, якщо покупку почали з попапа. */
  offer?: string;
}

export function useCreateInvoice() {
  return useMutation({
    mutationFn: (args: CreateInvoiceArgs | string) => {
      // Backward compat: якщо передали тільки slug як string
      const payload =
        typeof args === "string"
          ? { tariff_slug: args, use_bonus: false }
          : {
              tariff_slug: args.tariff_slug,
              use_bonus: args.use_bonus ?? false,
              // Ключ офера передаємо ЛИШЕ якщо людина прийшла за кнопкою з
              // попапа — тільки такий платіж отримує подарункові дні
              // (owner 2026-09-05).
              ...(args.offer ? { offer: args.offer } : {}),
            };
      return apiFetch<CreateInvoiceOut>("/payments/invoice", {
        method: "POST",
        body: payload,
      });
    },
  });
}

export interface FreezeTariff {
  slug: string;
  title_uk: string;
  days: number;
  amount: string;
}

export interface FreezeTariffsOut {
  items: FreezeTariff[];
}

export function useFreezeTariffs() {
  return useQuery({
    queryKey: ["subscription", "freeze", "tariffs"] as const,
    queryFn: () =>
      apiFetch<FreezeTariffsOut>("/subscription/freeze/tariffs"),
    staleTime: 5 * 60_000,
  });
}

/**
 * Створити WFP invoice для покупки freeze тарифа.
 * Юзер 2026-05-27: оплата заморозки через WFP.
 * Mini App відкриває purchase_url, після оплати webhook сам активує freeze.
 */
export function useFreezeInvoice() {
  return useMutation({
    mutationFn: (freeze_slug: string) =>
      apiFetch<{ order_reference: string; purchase_url: string }>(
        "/subscription/freeze/invoice",
        {
          method: "POST",
          body: { freeze_slug },
        },
      ),
  });
}

/**
 * Розморозити підписку достроково. Невикористані earned-balance дні
 * повертаються в баланс (тільки якщо freeze був з балансу).
 */
export function useFreezeUnfreeze() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{
        status: string;
        refunded_days: number;
        freeze_days_balance: number;
        message: string;
      }>("/subscription/freeze/unfreeze", { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.subscriptionMe });
    },
  });
}

/**
 * Заморозити підписку використовуючи накопичений freeze-баланс
 * (3 дні / 30 днів оплати — додаються автоматично при activate/extend).
 */
export function useFreezeUseEarned() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (days: number) =>
      apiFetch<{
        frozen_until: string | null;
        days: number;
        freeze_days_balance: number;
        message: string;
      }>("/subscription/freeze/use-earned", {
        method: "POST",
        // apiFetch \u0432\u0436\u0435 \u0440\u043e\u0431\u0438\u0442\u044c JSON.stringify(body). \u041f\u043e\u0434\u0432\u0456\u0439\u043d\u0438\u0439
        // stringify \u0432\u0438\u0434\u0430\u0432 string \u0437\u0430\u043c\u0456\u0441\u0442\u044c object \u2192 FastAPI 422.
        body: { days },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.subscriptionMe });
    },
  });
}

export interface PromoCheckOut {
  slug: string;
  days: number;
  amount: string;
  description: string;
  available: boolean;
  has_pending_invoice: boolean;
  can_create_invoice: boolean;
}

export function usePromoCheck(slug: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ["subscription", "promo", slug] as const,
    queryFn: () =>
      apiFetch<PromoCheckOut>(`/subscription/promo/${slug}/check`),
    enabled,
    staleTime: 60_000,
  });
}

/** Подарунок на день народження — три варіанти в одному річному вікні
 *  (owner 2026-09-13). Оплата будь-якого закриває решту до наступного ДР. */
export interface BirthdayGiftOption {
  slug: string;
  days: number;
  amount: string;
  /** Перекреслена стара ціна — null там, де її немає (подарунок за 1 ₴). */
  old_amount: string | null;
  /** Оранжева таблетка біля опису: «−55%», «1+1» або null. */
  pill: string | null;
  title: string;
  subtitle: string;
  has_pending_invoice: boolean;
}

/** Чому подарунок доступний чи ні (2026-09-26): «забрав» і «строк минув» —
 *  різні речі, а один `available` їх не розрізняв. */
export type BirthdayGiftState =
  | "available"
  | "redeemed"
  | "declined"
  | "expired"
  | "none";

export interface BirthdayGiftOut {
  available: boolean;
  /** Приходить з 2026-09-26; старий сервер його не віддає — див.
   *  `birthdayGiftState`. */
  state?: BirthdayGiftState;
  expires_at: string | null;
  options: BirthdayGiftOption[];
}

/** Стан подарунка з відповіді сервера. Якщо сервер ще старий і `state` не
 *  прислав — вгадуємо за таймером: строк позаду → «минув», інакше «забрав»
 *  (як було до 2026-09-26). */
export function birthdayGiftState(d: BirthdayGiftOut): BirthdayGiftState {
  if (d.state) return d.state;
  if (d.available) return "available";
  const ends = d.expires_at ? new Date(d.expires_at).getTime() : NaN;
  return Number.isFinite(ends) && ends <= Date.now() ? "expired" : "redeemed";
}

/** `enabled=false` — галерея попапів із мок-даними: запит не потрібен. */
export function useBirthdayGift(enabled = true) {
  return useQuery({
    queryKey: ["subscription", "birthday-gift"],
    queryFn: () => apiFetch<BirthdayGiftOut>("/subscription/birthday-gift"),
    staleTime: 30_000,
    enabled,
  });
}

/** Відмова від подарунка цього року — прибирає його звідусіль. */
export function useDeclineBirthdayGift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ ok: boolean }>("/subscription/birthday-gift/decline", {
        method: "POST",
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["subscription", "birthday-gift"] }),
  });
}

export function usePromoActivate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) =>
      apiFetch<CreateInvoiceOut>(
        `/subscription/promo/${slug}/activate`,
        { method: "POST" },
      ),
    onSuccess: (_data, slug) => {
      qc.invalidateQueries({ queryKey: ["subscription", "promo", slug] });
    },
  });
}

// ===================== AI-підтримка (чат) =====================
export interface SupportMessage {
  role: "user" | "assistant";
  content: string;
}
export interface SupportChatOut {
  reply: string;
}
export function useProfile() {
  return useQuery({
    queryKey: queryKeys.profile,
    queryFn: () => apiFetch<ProfileOut>("/profile/me"),
    staleTime: 30_000,
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ProfileUpdateIn) =>
      apiFetch<ProfileOut>("/profile/me", { method: "PUT", body: payload }),
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.profile, data);
    },
  });
}

// ============================================================
// Profile FULL — нова сторінка /profile в Mini App (bio + категорії + джерела)
// ============================================================

export interface SourcesState {
  telegram: boolean;
  chats: boolean;
  threads: boolean;
  fhunt: boolean;
  finder: boolean;  // InflowFinder — ексклюзивні вакансії від inFLOW
}

export type VacancyFormat = "original" | "summary";

// Формати роботи (owner 2026-07-23). unspecified завжди ввімкнено (не в списку).
export type WorkFormat = "online" | "offline" | "hybrid";

export interface ProfileFullOut extends ProfileOut {
  selected_category_ids: number[];
  sources: SourcesState;
  bio_min_chars: number;
  bio_max_chars: number;
  vacancy_format: VacancyFormat;
  work_formats: WorkFormat[];
  /** job / order — що отримувати (owner 2026-09-20). */
  work_kinds?: string[];
  /** Мови вакансій: uk | ru | en (owner 2026-09-19). */
  langs: string[];
}

export interface ProfileFullUpdateIn {
  bio?: string;
  selected_category_ids?: number[];
  sources?: Partial<SourcesState>;
  vacancy_format?: VacancyFormat;
  work_formats?: WorkFormat[];
  langs?: string[];
  portfolio_url?: string;
  ai_style_name?: string;
  ai_style_prompt?: string;
  /** День народження YYYY-MM-DD (owner 2026-07-23). Set-once на бекенді. */
  birthday?: string;
  /** Чорний список слів (owner 2026-09-06). */
  blocked_words?: string[];
}

export function useProfileFull() {
  return useQuery({
    queryKey: ["profile", "full"] as const,
    queryFn: () => apiFetch<ProfileFullOut>("/profile/full"),
    staleTime: 30_000,
  });
}

export function useUpdateProfileFull() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ProfileFullUpdateIn) =>
      apiFetch<ProfileFullOut>("/profile/full", { method: "PUT", body: payload }),
    // Оптимістичне оновлення: миттєво патчимо кеш профілю ПЕРЕД мережею,
    // щоб тоггл сфери/джерела ставав активним одразу, а не після відповіді
    // сервера (юзер 2026-06-11: «вібрує, а оранжевим стає аж за кілька сек»).
    onMutate: async (payload: ProfileFullUpdateIn) => {
      await qc.cancelQueries({ queryKey: ["profile", "full"] });
      const prev = qc.getQueryData<ProfileFullOut>(["profile", "full"]);
      if (prev) {
        qc.setQueryData<ProfileFullOut>(["profile", "full"], {
          ...prev,
          ...payload,
          // sources у payload — Partial; зливаємо в повний об'єкт prev.
          sources: payload.sources
            ? { ...prev.sources, ...payload.sources }
            : prev.sources,
        });
      }
      return { prev };
    },
    onError: (_err, _payload, ctx) => {
      const prev = (ctx as { prev?: ProfileFullOut } | undefined)?.prev;
      if (prev) qc.setQueryData(["profile", "full"], prev);
    },
    onSuccess: (data) => {
      qc.setQueryData(["profile", "full"], data);
      qc.invalidateQueries({ queryKey: queryKeys.profile });
      qc.invalidateQueries({ queryKey: queryKeys.categories });
      // Юзер 2026-05-29: швидкі фільтри прямо у Feed → при зміні категорій/
      // джерел стрічка має оновитись з новими фільтрами.
      qc.invalidateQueries({ queryKey: ["feed"] });
    },
  });
}

export function useSubmitUserGroups() {
  return useMutation({
    mutationFn: (text: string) =>
      apiFetch<{ accepted: boolean }>("/user-groups/submit", {
        method: "POST",
        body: { text },
      }),
  });
}

// ============================================================
// Feed (нова сторінка вакансій)
// ============================================================

export interface ParsedContacts {
  telegrams: string[];
  phones: string[];
  emails: string[];
  urls: string[];
  /** Map username → display name (для telegram_chat «Контакт: Name @username»). */
  tg_names?: Record<string, string>;
}

export interface FeedItemOut {
  id: number;
  category_id: number | null;
  category_name: string | null;
  source: string;
  ai_summary: string | null;
  text_preview: string;
  captured_at: string;
  has_contacts: boolean;
  work_format: string; // online | offline | hybrid | unspecified
  /** Факти з тексту вакансії (owner 2026-09-20). Порожньо = у тексті не було. */
  pay?: string | null;
  experience?: string | null;
  location?: string | null;
  /** job — шукає компанія, order — людина під задачу, null — не визначено. */
  work_kind?: string | null;
  client_rating_avg: number | null;
  client_rating_count: number;
  client_reviews_count: number;
}

export interface FeedListOut {
  items: FeedItemOut[];
  has_more: boolean;
  next_cursor: string | null;
  total?: number;
}

export interface FeedVacancyOut {
  id: number;
  category_id: number | null;
  category_name: string | null;
  /** Усі сфери вакансії, головна першою (owner 2026-08-28). */
  category_names?: string[];
  source: string;
  ai_summary: string | null;
  text: string;
  captured_at: string;
  work_format: string; // online | offline | hybrid | unspecified
  /** Факти з тексту вакансії (owner 2026-09-20). Порожньо = у тексті не було. */
  pay?: string | null;
  experience?: string | null;
  location?: string | null;
  /** job — шукає компанія, order — людина під задачу, null — не визначено. */
  work_kind?: string | null;
  contact_username: string | null;
  /** Замовник для оцінок: у Telegram — той самий юзернейм, у Threads — нік
   *  автора поста (owner 2026-08-27). */
  customer_handle: string | null;
  customer_platform: string;
  text_html?: string | null;
  contacts: ParsedContacts;
  /** Посилання на оригінальний пост у групі — контакт вакансії з чату без
   *  ніка, телефону й пошти (owner 2026-09-24). Рахує сервер. */
  post_url?: string | null;
  contacts_unlocked: boolean;
  attempts_left_today: number;
  client_rating_avg: number | null;
  client_rating_count: number;
  client_reviews_count: number;
  is_blacklisted: boolean;
  my_rating: number | null;
  /** Чи вакансія у збережених цієї людини (owner 2026-09-06). */
  is_saved?: boolean;
  /** Файли, які замовник прикріпив до вакансії (owner 2026-09-12). */
  media?: VacancyMedia[];
  /** Вакансію знято (адмін або дві скарги). Тоді текст і контакти порожні,
   *  а сторінка показує «Цю вакансію знято» (owner 2026-09-25). */
  removed?: boolean;
}

export interface VacancyMedia {
  id: number;
  kind: string; // photo | video | document
  mime: string | null;
  filename: string | null;
  size: number | null;
  /** Шлях нашого API — віддає файл із автентифікацією. */
  url: string;
  /** Те саме з підписом на годину — для системного переглядача Telegram. */
  open_url?: string;
}

export interface UnlockContactsOut {
  ok: boolean;
  attempts_left: number;
  contacts: ParsedContacts | null;
  /** Чи списалось одне з пʼяти тижневих відкриттів САМЕ цим викликом. */
  spent?: boolean;
}

export interface ClientReviewItem {
  id: number;
  rating: number;
  review_text: string | null;
  is_legacy: boolean;
  created_at: string;
}

export interface ClientReviewsOut {
  contact_username: string;
  avg_rating: number | null;
  count: number;
  items: ClientReviewItem[];
}

export function useFeedFirstPage(limit: number = 20) {
  return useQuery({
    queryKey: ["feed", "page-1", limit] as const,
    queryFn: () => apiFetch<FeedListOut>(`/feed?limit=${limit}`),
    // staleTime 30s → 0 + refetchOnMount. Юзер 2026-05-23: «в боті вакансія
    // вже прийшла, у /feed ще нема». 30s кеш тримав старі дані до 30 сек.
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
  });
}

/**
 * Пошук вакансій по всій базі за 30 днів (юзер 2026-06-06).
 * Submit-based: викликається коли юзер ввів і підтвердив запит.
 * Підтримує offset-пагінацію (fetchSearchPage для load-more).
 */
export function useSearchFeed(query: string) {
  const trimmed = query.trim();
  return useQuery({
    queryKey: ["feed", "search", trimmed] as const,
    queryFn: () =>
      apiFetch<FeedListOut>(`/feed/search?q=${encodeURIComponent(trimmed)}`),
    enabled: trimmed.length >= 2,
    staleTime: 30_000,
  });
}

export function fetchSearchPage(query: string, offset: number) {
  return apiFetch<FeedListOut>(
    `/feed/search?q=${encodeURIComponent(query.trim())}&offset=${offset}`,
  );
}

export function useFeedNewCount(since: string | null) {
  return useQuery({
    queryKey: ["feed", "new-count", since] as const,
    queryFn: () => {
      const q = since ? `?since=${encodeURIComponent(since)}` : "";
      return apiFetch<{ count: number }>(`/feed/new-count${q}`);
    },
    staleTime: 60_000,
  });
}

export async function fetchFeedPage(cursor: string, limit: number = 20): Promise<FeedListOut> {
  const q = new URLSearchParams({ cursor, limit: String(limit) });
  return apiFetch<FeedListOut>(`/feed?${q.toString()}`);
}

export function useFeedVacancy(vacancyId: number) {
  return useQuery({
    queryKey: ["feed", "vacancy", vacancyId] as const,
    queryFn: () => apiFetch<FeedVacancyOut>(`/feed/${vacancyId}`),
    staleTime: 15_000,
  });
}

export function useUnlockContacts(vacancyId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<UnlockContactsOut>(`/feed/${vacancyId}/unlock-contacts`, {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["feed", "vacancy", vacancyId] });
    },
  });
}

/**
 * «Це не вакансія» (юзер 2026-06-25): скарга. 1 → ховає в цього юзера;
 * 2 різних → прибирає глобально. Інвалідує feed.
 */
/**
 * «Видалити вакансію» — прибрати її ОСОБИСТО в себе (owner 2026-08-15).
 * Не скарга: інших людей не зачіпає, статистику не змінює. Вакансія зникає
 * зі стрічки й пошуку саме в цієї людини.
 */
export function useHideVacancy(vacancyId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ status: string }>(`/feed/${vacancyId}/hide`, {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["feed"] });
    },
  });
}

/**
 * «Зберегти» — перемикач (owner 2026-09-06). Один ендпоінт на обидві дії:
 * сервер сам вирішує, зберегти чи прибрати, і повертає новий стан.
 */
export function useToggleSaveVacancy(vacancyId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ saved: boolean; vacancy_id: number }>(
        `/feed/${vacancyId}/save`,
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["feed", "vacancy", vacancyId] });
      qc.invalidateQueries({ queryKey: ["feed", "saved"] });
    },
  });
}

/** Список збережених вакансій — найновіші зверху. */
export function useSavedVacancies() {
  return useQuery({
    queryKey: ["feed", "saved"],
    queryFn: () => apiFetch<FeedListOut>("/feed/saved/list?limit=50"),
  });
}

export function useFlagNotVacancy(vacancyId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ ok: boolean; status: string }>(
        `/feed/${vacancyId}/not-vacancy`,
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["feed"] });
    },
  });
}

/**
 * АДМІН: зняти вакансію глобально (owner 2026-08-06). Прибирає її зі стрічки
 * у ВСІХ і ставить фонове видалення вже надісланих повідомлень у ботах.
 * Незворотно — виклик лише з підтвердженням.
 */
export function useAdminRemoveVacancy(vacancyId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ ok: boolean; already_removed: boolean }>(
        `/admin/vacancies/${vacancyId}/remove`,
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["feed"] });
    },
  });
}

/**
 * АДМІН: ручна заморозка / розморозка підписки (owner 2026-08-10).
 * Людині одразу летить повідомлення в бот.
 */
export function useAdminFreeze(userId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ ok: boolean; recurring_disabled: boolean }>(
        `/admin/users/${userId}/freeze`,
        { method: "POST" },
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["admin", "user-profile", userId] }),
  });
}

export function useAdminUnfreeze(userId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ ok: boolean }>(`/admin/users/${userId}/unfreeze`, {
        method: "POST",
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["admin", "user-profile", userId] }),
  });
}

import type { ReviewSubmitResult } from "@/lib/reviewReward";

export function useVacancyReviews(vacancyId: number, enabled: boolean) {
  return useQuery({
    queryKey: ["feed", "reviews", vacancyId] as const,
    queryFn: () => apiFetch<ClientReviewsOut>(`/feed/${vacancyId}/reviews`),
    enabled,
    staleTime: 30_000,
  });
}

export function useSubmitReview(vacancyId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { rating: number; review_text?: string | null }) =>
      apiFetch<ReviewSubmitResult>(`/feed/${vacancyId}/review`, {
        method: "POST",
        body: data,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["feed", "reviews", vacancyId] });
      qc.invalidateQueries({ queryKey: ["feed", "vacancy", vacancyId] });
      // За першу оцінку замовника нараховується 1 ₴ (owner 2026-08-27) —
      // бонусний рахунок на /quests має показати нову суму одразу.
      qc.invalidateQueries({ queryKey: queryKeys.quests });
    },
  });
}

/** Адмін видаляє ЧУЖИЙ відгук (owner 2026-08-31): у стрічці — по id відгуку. */
export function useAdminDeleteReview(vacancyId?: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reviewId: number) =>
      apiFetch<{ ok: boolean; platform: string; handle: string }>(
        `/admin/reviews/${reviewId}`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      if (vacancyId) {
        qc.invalidateQueries({ queryKey: ["feed", "reviews", vacancyId] });
        qc.invalidateQueries({ queryKey: ["feed", "vacancy", vacancyId] });
      }
      qc.invalidateQueries({ queryKey: ["admin", "customers"] });
    },
  });
}

export type AdminCustomer = {
  platform: string;
  handle: string;
  rating_avg: number | null;
  rating_count: number;
  reviews: number;
  vacancies: number;
};

export type FinderVacancyStats = {
  day: number;
  week: number;
  month: number;
  total: number;
  removed: number;
  delivered_month: number;
  /** Дії людей за 30 днів по вакансіях Finder — чи джерело дає віддачу. */
  opened_month: number;
  saved_month: number;
  ai_month: number;
  complaints_month: number;
  /** Скільки вакансій дали ВСІ джерела за 30 днів — для частки Finder. */
  flow_total_month: number;
  by_day: { date: string; count: number }[];
  by_sphere: { name: string; count: number }[];
  by_kind: { name: string; count: number }[];
  by_source: { name: string; count: number }[];
};

export type UnlockActivity = {
  limit: number;
  day: number;
  week7: number;
  month: number;
  people_week7: number;
  people_month: number;
  wk_people: number;
  wk_exhausted: number;
  wk_opens: number;
  /** Скільки людей витратили 1, 2, 3, 4 відкриття цього тижня. */
  wk_spread: number[];
  wk_exhausted_paid: number;
  by_source: { name: string; count: number }[];
  by_day: { date: string; count: number }[];
};

export type AiFlagged = {
  by_kind: { name: string; week: number; month: number }[];
  items: {
    id: number;
    kind: string;
    at: string;
    text: string;
    summary: string;
    removed: boolean;
  }[];
};

type ActivityCount = {
  day: number;
  week: number;
  month: number;
  total: number;
  people_month: number;
};

export type AdminActivity = {
  quests: {
    done: number;
    people: number;
    legacy_done: number;
    done_month: number;
    started: number;
    in_progress: number;
    /** Сума, порахована з `metadata.thresholds_paid` за каталогом нагород:
     *  колонка `reward_bonus` заповнена лише в одному квесті зі ста. */
    reward_uah: number;
    thresholds_paid: number;
  };
  quests_by_type: {
    name: string;
    done: number;
    people: number;
    started: number;
    thresholds: number;
  }[];
  ai: ActivityCount;
  reviews: {
    total: number;
    avg: number;
    with_text: number;
    month: number;
    people: number;
  };
  saved: ActivityCount;
  complaints: ActivityCount;
  support: ActivityCount;
  unlocks: ActivityCount;
  spins: ActivityCount;
  course: {
    registrations: number;
    lesson_views: number;
    viewers: number;
    votes: number;
    avg_vote: number;
  };
  touches: { name: string; sent: number; people: number; errors: number }[];
};

/** Активність людей у продукті (owner 2026-09-12). */
export function useAdminActivity() {
  return useQuery({
    queryKey: ["admin", "activity"] as const,
    queryFn: () => apiFetch<AdminActivity>("/admin/activity"),
    staleTime: 60_000,
  });
}

/** Пости, які ІІ не вважає вакансією (owner 2026-09-12). */
export function useAiFlagged() {
  return useQuery({
    queryKey: ["admin", "ai-flagged"] as const,
    queryFn: () => apiFetch<AiFlagged>("/admin/ai-flagged"),
    staleTime: 60_000,
  });
}

/** Активність безкоштовних: відкриття контактів (owner 2026-09-11). */
export function useUnlockActivity() {
  return useQuery({
    queryKey: ["admin", "unlocks"] as const,
    queryFn: () => apiFetch<UnlockActivity>("/admin/unlocks"),
    staleTime: 60_000,
  });
}

export function useFinderVacancyStats() {
  return useQuery({
    queryKey: ["admin", "finder-vacancies"] as const,
    queryFn: () => apiFetch<FinderVacancyStats>("/admin/finder/vacancies"),
    staleTime: 60_000,
  });
}

export type AdminCustomerSort = "rating_low" | "rating_high" | "reviews" | "count";

export function useAdminCustomers(
  q: string,
  sort: AdminCustomerSort = "rating_low",
  limit = 50,
) {
  return useQuery({
    queryKey: ["admin", "customers", q, sort, limit] as const,
    queryFn: () =>
      apiFetch<{ total: number; items: AdminCustomer[] }>(
        `/admin/customers?q=${encodeURIComponent(q)}&sort=${sort}&limit=${limit}`,
      ),
    staleTime: 15_000,
  });
}

export type AdminCustomerReview = {
  id: number;
  rating: number;
  text: string;
  created_at: string | null;
  author_username: string | null;
  vacancy_id: number | null;
};

export function useAdminCustomerReviews(
  platform: string | null,
  handle: string | null,
) {
  return useQuery({
    queryKey: ["admin", "customer-reviews", platform, handle] as const,
    queryFn: () =>
      apiFetch<{ items: AdminCustomerReview[] }>(
        `/admin/customers/${platform}/${encodeURIComponent(handle ?? "")}/reviews`,
      ),
    enabled: !!platform && !!handle,
    staleTime: 10_000,
  });
}

export function useDeleteReview(vacancyId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ ok: boolean; review_id: number }>(`/feed/${vacancyId}/review`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["feed", "reviews", vacancyId] });
      qc.invalidateQueries({ queryKey: ["feed", "vacancy", vacancyId] });
    },
  });
}

export function useReferrals() {
  return useQuery({
    queryKey: queryKeys.referrals,
    queryFn: () => apiFetch<ReferralsOut>("/referrals/me"),
    staleTime: 30_000,
  });
}

export interface ReferralsListPage {
  items: import("./types").ReferralItem[];
  offset: number;
  has_more: boolean;
  total: number;
}

import { useInfiniteQuery } from "@tanstack/react-query";

export interface WithdrawalRequestOut {
  requested_uah: number;
  remaining_balance_uah: number;
  /** `•••• 1234` — повний номер сервер назад не віддає. */
  card_mask: string;
  payout_id: number;
  message: string;
}

// Owner 2026-10-19: вивід «як у Findly» — заявка з номером картки.
// Мої посилання (owner 2026-10-25, «як у кабінеті партнера Findly»).
export function useReferralLinks(enabled: boolean) {
  return useQuery({
    queryKey: ["referrals", "links"] as const,
    queryFn: () => apiFetch<import("./types").ReferralLinksOut>("/referrals/links"),
    enabled,
    staleTime: 30_000,
  });
}

export function useCreateReferralLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      apiFetch<import("./types").ReferralLink>("/referrals/links", {
        method: "POST",
        body: { name },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["referrals", "links"] }),
  });
}

export function useUpdateReferralLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: number; name?: string; archived?: boolean }) =>
      apiFetch<import("./types").ReferralLink>(`/referrals/links/${v.id}`, {
        method: "PATCH",
        body: { name: v.name, archived: v.archived },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["referrals", "links"] }),
  });
}

export function useRequestWithdrawal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (card: string) =>
      apiFetch<WithdrawalRequestOut>("/referrals/withdraw", {
        method: "POST",
        body: { card },
        // Сервер після заявки ще кладе картку в сервісний канал (тротл 1 / 5 с).
        // Дефолтних 15 с може не вистачити — людина побачила б «мережева
        // помилка», хоча заявку вже прийнято, і натиснула б ще раз.
        timeoutMs: 30_000,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.referrals });
    },
  });
}

// Обмін реф-балансу на підписку (owner 2026-07-24, 25₴/день).
export interface ExchangeQuoteOut {
  day_price_uah: number;
  balance_uah: number;
  days: number;
  /** Максимум днів, який дозволяє баланс (days — скільки просив юзер). */
  max_days: number;
  cost_uah: number;
  remaining_uah: number;
}
export interface ExchangeSubscriptionOut {
  days: number;
  cost_uah: number;
  remaining_balance_uah: number;
  ends_at: string;
}

export function useExchangeQuote(enabled: boolean) {
  return useQuery({
    queryKey: ["referral-exchange-quote"],
    queryFn: () => apiFetch<ExchangeQuoteOut>("/referrals/exchange-quote"),
    enabled,
    staleTime: 0,
  });
}

export function useExchangeSubscription() {
  const qc = useQueryClient();
  return useMutation({
    // days — скільки саме днів бере юзер (owner 2026-07-27). undefined = максимум.
    mutationFn: (days?: number) =>
      apiFetch<ExchangeSubscriptionOut>(
        "/referrals/exchange-subscription" +
          (days ? `?days=${encodeURIComponent(days)}` : ""),
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.referrals });
      qc.invalidateQueries({ queryKey: ["referral-exchange-quote"] });
      qc.invalidateQueries({ queryKey: queryKeys.subscriptionMe });
    },
  });
}

export function useReferralsPaginated(pageSize: number = 10) {
  return useInfiniteQuery({
    queryKey: ["referrals", "list", pageSize],
    queryFn: ({ pageParam = 0 }) =>
      apiFetch<ReferralsListPage>(
        `/referrals/list?offset=${pageParam}&limit=${pageSize}`,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.offset + lastPage.items.length : undefined,
    staleTime: 30_000,
  });
}

export function useQuests() {
  return useQuery({
    queryKey: queryKeys.quests,
    queryFn: () => apiFetch<QuestsListOut>("/quests/me"),
    staleTime: 30_000,
  });
}

/** Підписочні квести (підписка на канали/чат → +50₴ кожен). */
export function useSubscriptionQuests() {
  return useQuery({
    queryKey: queryKeys.subscriptionQuests,
    queryFn: () =>
      apiFetch<SubscriptionQuestsOut>("/quests/subscriptions"),
    staleTime: 30_000,
  });
}

/** Перевірити підписку на один канал/чат → нарахувати 50₴, якщо треба. */
export function useCheckSubscriptionQuest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: string) =>
      apiFetch<SubscriptionCheckOut>("/quests/subscriptions/check", {
        method: "POST",
        body: { key },
      }),
    onSuccess: (data) => {
      // Оновлюємо стан підписок + баланс квестів (regular) якщо нарахували.
      qc.invalidateQueries({ queryKey: queryKeys.subscriptionQuests });
      if (data.status === "credited") {
        qc.invalidateQueries({ queryKey: queryKeys.quests });
      }
    },
  });
}

export function useLessons() {
  return useQuery({
    queryKey: ["course", "lessons"],
    queryFn: () => apiFetch<LessonsListOut>("/course/lessons"),
    staleTime: 5 * 60_000,
  });
}

// ============================================================
// Course — 6 уроків від засновника + ефір (1.0 course_*)
// ============================================================

export interface CourseLessonOut {
  idx: number;
  title: string;
  is_finale: boolean;
  unlocked: boolean;
  /** Чи переглянув юзер цей урок (перший play відео). */
  viewed: boolean;
  video_file_id: string | null;
}

export interface CourseListOut {
  welcome_text: string;
  not_available_text: string;
  days_passed: number;
  /** Всього пунктів курсу (6 уроків + ефір = 7). */
  total: number;
  /** Скільки переглянуто (для % прогресу). */
  viewed_count: number;
  items: CourseLessonOut[];
}

export function useCourse() {
  return useQuery({
    queryKey: ["course"],
    queryFn: () => apiFetch<CourseListOut>("/course/"),
    staleTime: 30_000,
  });
}

/** Позначити урок курсу переглянутим (idempotent, при першому play відео). */
export function useMarkCourseLessonViewed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lessonIdx: number) =>
      apiFetch<{ ok: boolean; viewed_count: number; total: number }>(
        `/course/lessons/${lessonIdx}/viewed`,
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["course"] });
    },
  });
}

export function useKBSections() {
  return useQuery({
    queryKey: ["kb", "sections"],
    queryFn: () => apiFetch<KBSectionsResponse>("/knowledge-base/sections"),
    staleTime: 5 * 60_000,
  });
}

/** Лічильники Бази знань для переліку переваг — відкриті й без підписки. */
export function useKBCounts() {
  return useQuery({
    queryKey: ["kb", "counts"],
    queryFn: () => apiFetch<KBCountsOut>("/knowledge-base/counts"),
    staleTime: 5 * 60_000,
  });
}

export function useKBArticles() {
  return useQuery({
    queryKey: ["kb", "articles"],
    queryFn: () =>
      apiFetch<KBArticleCategoryOut[]>("/knowledge-base/articles"),
    staleTime: 30 * 60_000,
  });
}

/** Стаття з БД `knowledge_base_articles` (мігровано з 1.0 `know_base`).
 *  18 admin-керованих статей. На відміну від статичних telegra.ph,
 *  ці редагуються прямо в Postgres. UI показує їх окремою секцією
 *  на сторінці Бази знань ЯКЩО список не порожній. */
export interface KBStoredArticleOut {
  id: number;
  title: string;
  content: string;
  photo_file_id: string | null;
  sort_order: number | null;
}

export function useKBDbArticles() {
  return useQuery({
    queryKey: ["kb", "db-articles"],
    queryFn: () =>
      apiFetch<KBStoredArticleOut[]>("/knowledge-base/db-articles"),
    staleTime: 30 * 60_000,
  });
}

export function useKBDbArticle(id: number | string | undefined) {
  return useQuery({
    queryKey: ["kb", "db-articles", id],
    queryFn: () =>
      apiFetch<KBStoredArticleOut>(`/knowledge-base/db-articles/${id}`),
    enabled: id !== undefined && id !== "",
    staleTime: 30 * 60_000,
  });
}

export function useKBLessonCategories() {
  return useQuery({
    queryKey: ["kb", "lesson-categories"],
    queryFn: () =>
      apiFetch<KBLessonCategoryOut[]>("/knowledge-base/lesson-categories"),
    staleTime: 5 * 60_000,
  });
}

export function useKBLessons(categoryIdx: number | null) {
  return useQuery({
    queryKey: ["kb", "lessons", categoryIdx ?? "all"],
    queryFn: () => {
      const q = categoryIdx != null ? `?category_idx=${categoryIdx}` : "";
      return apiFetch<KBLessonsListOut>(`/knowledge-base/lessons${q}`);
    },
    enabled: categoryIdx !== null,
    staleTime: 60_000,
  });
}

/** Усі уроки без фільтра — потрібно для диплінка «відкрити урок N»,
 *  коли категорія уроку ще невідома (owner 2026-09-12: кнопка в листі
 *  про новий урок має відкривати САМЕ цей урок). */
export function useKBLessonsAll(enabled: boolean) {
  return useQuery({
    queryKey: ["kb", "lessons", "all"],
    queryFn: () => apiFetch<KBLessonsListOut>("/knowledge-base/lessons"),
    enabled,
    staleTime: 60_000,
  });
}

/** Позначити урок бази знань переглянутим — після 15 секунд відтворення
 *  (owner 2026-09-17). Ідемпотентно; прогрес квестів читає ці відмітки. */
export function useMarkKBLessonViewed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { lessonId: number; seconds: number }) =>
      apiFetch<{
        ok: boolean;
        counted: boolean;
        viewed_count: number;
        total_count: number;
      }>(`/knowledge-base/lessons/${vars.lessonId}/viewed`, {
        method: "POST",
        body: { seconds: vars.seconds },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["kb", "lessons"] });
      qc.invalidateQueries({ queryKey: ["quests"] });
    },
  });
}

export function useVoteKBLesson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ lessonId, rating }: { lessonId: number; rating: number }) =>
      apiFetch<VoteLessonOut>(
        `/knowledge-base/lessons/${lessonId}/vote`,
        { method: "POST", body: { rating } },
      ),
    onSuccess: () => {
      // Інвалідуємо всі lesson-списки щоб avg/my_rating оновились
      qc.invalidateQueries({ queryKey: ["kb", "lessons"] });
      // Баланс міг змінитись (+10₴ за перший vote)
      qc.invalidateQueries({ queryKey: queryKeys.referrals });
      qc.invalidateQueries({ queryKey: queryKeys.quests });
    },
  });
}

export function useAdminStats() {
  return useQuery({
    queryKey: ["admin", "stats"],
    queryFn: () => apiFetch<AdminStatsOut>("/admin/stats"),
    staleTime: 30_000,
  });
}

export function useAdminDashboard(range: AdminRange, cohort: string = "all") {
  return useQuery({
    queryKey: ["admin", "dashboard", range, cohort],
    queryFn: () =>
      apiFetch<AdminDashboardOut>(
        `/admin/dashboard?range=${range}&cohort=${encodeURIComponent(cohort)}`,
      ),
    staleTime: 30_000,
  });
}

export function useAdminFunnel(range: AdminRange) {
  return useQuery({
    queryKey: ["admin", "funnel", range],
    queryFn: () => apiFetch<AdminFunnelOut>(`/admin/funnel?range=${range}`),
    staleTime: 30_000,
  });
}

export function useAdminPromocodes() {
  return useQuery({
    queryKey: ["admin", "promocodes"],
    queryFn: () => apiFetch<AdminPromoListOut>("/admin/promocodes"),
    staleTime: 10_000,
  });
}

export function useCreatePromocode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { slug: string; days: number; amount: number; name: string }) =>
      apiFetch<AdminPromoItem>("/admin/promocodes", { method: "POST", body: p }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "promocodes"] }),
  });
}

export function useTogglePromocode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) =>
      apiFetch(`/admin/promocodes/${slug}/toggle`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "promocodes"] }),
  });
}

export function useDeletePromocode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) =>
      apiFetch(`/admin/promocodes/${slug}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "promocodes"] }),
  });
}

/** «Журнал дій» людини (адмінка → Чарджбеки, S57). Лише за явним відкриттям. */
export function useAdminUserJournal(userId: number | null, enabled = true) {
  return useQuery({
    queryKey: ["admin", "user-journal", userId],
    queryFn: () =>
      apiFetch<UserJournalOut>(`/admin/chargeback/journal?user_id=${userId}`),
    enabled: enabled && userId != null,
    staleTime: 30_000,
  });
}

export function useAdminUserProfile(userId: number | null) {
  return useQuery({
    queryKey: ["admin", "user-profile", userId],
    queryFn: () =>
      apiFetch<AdminUserProfileOut>(`/admin/users/${userId}/profile`),
    enabled: userId != null,
    staleTime: 10_000,
  });
}

/**
 * Адмін-дії над юзером (блок/розблок/баланс/повідомлення).
 * Усі інвалідовують профіль `["admin","user-profile",userId]` щоб UI
 * одразу показав новий стан (бан-чіп, баланс).
 */
export function useAdminBlockUser() {
  const qc = useQueryClient();
  return useMutation({
    // Бан знімає ще й регулярки WayForPay (owner 2026-09-29) — підсумок
    // приходить в `autorenew`, адмінка показує його одразу після бану.
    mutationFn: (userId: number) =>
      apiFetch<{ status: string; autorenew?: RegularRemovalOut }>(
        `/admin/users/${userId}/block`,
        { method: "POST" },
      ),
    onSuccess: (_d, userId) => {
      qc.invalidateQueries({ queryKey: ["admin", "user-profile", userId] });
    },
  });
}

export function useAdminUnblockUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: number) =>
      apiFetch<{ ok: boolean }>(`/admin/users/${userId}/unblock`, { method: "POST" }),
    onSuccess: (_d, userId) => {
      qc.invalidateQueries({ queryKey: ["admin", "user-profile", userId] });
    },
  });
}

export interface AdminAdjustBalanceOut {
  referral_balance_uah: number;
  regular_balance_uah: number;
}

export function useAdminAdjustBalance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { userId: number; ref_delta?: number; reg_delta?: number }) =>
      apiFetch<AdminAdjustBalanceOut>(`/admin/users/${vars.userId}/adjust-balance`, {
        method: "POST",
        body: { ref_delta: vars.ref_delta ?? 0, reg_delta: vars.reg_delta ?? 0 },
      }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["admin", "user-profile", vars.userId] });
    },
  });
}

/** Виправити людині дату народження з адмінки (owner 2026-09-09). */
/** Екран замків: скільки вакансій чекає і три СПРАВЖНІ картки стрічки. */
export function useLockedPreview() {
  return useQuery({
    queryKey: ["onboarding", "locked-preview"],
    queryFn: () =>
      apiFetch<{
        total: number;
        items: FeedItemOut[];
        sphere_names: string[];
      }>("/onboarding/locked-preview"),
    staleTime: 60_000,
  });
}

export function useAdminSetBirthday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { userId: number; birthday: string | null }) =>
      apiFetch<{ ok: boolean; birthday: string | null }>(
        `/admin/users/${vars.userId}/birthday`,
        { method: "POST", body: { birthday: vars.birthday } },
      ),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["admin", "user-profile", vars.userId] });
    },
  });
}

export function useAdminMessageUser() {
  return useMutation({
    mutationFn: (vars: { userId: number; text: string }) =>
      apiFetch<{ ok: boolean }>(`/admin/users/${vars.userId}/message`, {
        method: "POST",
        body: { text: vars.text },
      }),
  });
}

export function useAdminUsers(q: string) {
  return useQuery({
    queryKey: ["admin", "users", q],
    queryFn: () =>
      apiFetch<AdminUserSearchOut>(
        `/admin/users?q=${encodeURIComponent(q)}&limit=30`,
      ),
    staleTime: 10_000,
  });
}

export interface FinderStatsOut {
  ok: boolean;
  total?: number;
  active?: number;
  blocked?: number;
  reason?: string;
}

/** Статистика inFLOW Finder — окремий сервіс (owner 2026-08-29). */
/** Аналітика колеса фортуни для адмінки (owner 2026-09-04). */
export interface AdminWheelOut {
  period_days: number;
  granted: number;
  spun: number;
  pending: number;
  by_prize: { prize: string; title?: string; count: number }[];
  promo_2m: { issued: number; paid: number };
  days_given: number;
  bonuses_given: number;
  /** Воронка подвоєння призу за підписку на Instagram inFLOW (owner 2026-10-27, 04.11): люди серед тих,
   *  кому показали кнопку. */
  double?: {
    offered: number;
    started: number;
    declined: number;
    insta: number;
    submitted: number;
    submits: number;
    decided: number;
    doubled: number;
    rejected: number;
    waiting: number;
    days_added: number;
    bonuses_added: number;
    /** Чому заявку не прийняли одразу. */
    reasons: { reason: string; attempts: number; people: number }[];
    /** Чому відмовили після перевірки. */
    refusals: { reason: string; attempts: number; people: number }[];
  };
}

export function useAdminWheel(days = 30) {
  return useQuery({
    queryKey: ["admin", "wheel", days] as const,
    queryFn: () => apiFetch<AdminWheelOut>(`/admin/wheel?days=${days}`),
    staleTime: 60_000,
  });
}

/** Аналітика «MCP від inFLOW» для адмінки (owner 2026-11-01). Сервер — `services/mcp_stats.py`. */
export interface AdminMcpUser {
  user_id: number;
  username: string | null;
  first_name: string | null;
}

export interface AdminMcpOut {
  period_days: number;
  /** Стан на зараз, за весь час. */
  connections: {
    ever: number;
    enabled: number;
    connected: number;
    used: number;
    turned_off: number;
    blocked: number;
    new: number;
    enabled_paid: number;
    seen_7d: number;
    enabled_connected: number;
  };
  clients: { client: string; people: number }[];
  usage: {
    calls: number;
    people: number;
    returning: number;
    by_tool: { tool: string; calls: number }[];
    outcomes: Record<string, number>;
    daily: { date: string; calls: number; people: number; served: number; full_texts: number }[];
  };
  served: {
    rows: number;
    vacancies: number;
    full_texts: number;
    people: number;
    cap_days: number;
    full_cap_days: number;
  };
  unlocked: { contacts: number; people: number };
  first_paid: number;
  top: (AdminMcpUser & {
    client: string | null;
    calls_total: number;
    last_used_at: string | null;
    enabled: boolean;
    blocked: boolean;
    paid: boolean;
    served: number;
  })[];
  blocked: (AdminMcpUser & { reason: string | null; until: string | null })[];
  caps: { vacancies_free: number; vacancies_paid: number; full_free: number; full_paid: number };
}

export function useAdminMcp(days: number) {
  return useQuery({
    queryKey: ["admin", "mcp", days] as const,
    queryFn: () => apiFetch<AdminMcpOut>(`/admin/mcp?days=${days}`),
    // Перемикання періоду не ховає вкладку за спінер — старі цифри стоять, поки йдуть нові.
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

/** Дні народження і подарунки для адмінки (owner 2026-09-20). */
export interface AdminBirthdaysOut {
  birthdays: {
    total: number;
    d3: number;
    d7: number;
    d30: number;
    no_set_at: number;
    users_total: number;
  };
  gifts: {
    offered: number;
    redeemed: number;
    declined: number;
    offered_year: number;
    redeemed_year: number;
    offered_30: number;
    redeemed_30: number;
  };
  by_gift: { slug: string; count: number }[];
}

export function useAdminBirthdays() {
  return useQuery({
    queryKey: ["admin", "birthdays"] as const,
    queryFn: () => apiFetch<AdminBirthdaysOut>("/admin/birthdays"),
    staleTime: 60_000,
  });
}

/** AI-підтримка в лічці для адмінки (owner 2026-09-25, аудит Findly п.29). */
export interface AdminSupportUnanswered {
  chat_id: number;
  who: string;
  reason: string;
  count: number;
  kind: string;
  text: string;
  first_at: string;
  last_at: string;
}

export interface AdminSupportOut {
  paused: boolean;
  connection: {
    status: "ok" | "disconnected" | "no_reply" | "unknown";
    owner: string;
    connected_at: string | null;
    checked_at: number | null;
  };
  silence_minutes: number;
  silenced: { chat_id: number; who: string; minutes_left: number }[];
  daily_limit: number;
  openai_key: "separate" | "shared" | "missing";
  journal: {
    hours: number;
    incoming: number;
    answered: number;
    chats: number;
    in_work: number;
    unanswered_chats: number;
    not_users: number;
    limit_hit_today: number;
    reasons: { reason: string; chats: number }[];
    unanswered: AdminSupportUnanswered[];
  } | null;
}

export function useAdminSupport() {
  return useQuery({
    queryKey: ["admin", "support"] as const,
    queryFn: () => apiFetch<AdminSupportOut>("/admin/support"),
    staleTime: 30_000,
  });
}

export function useAdminSupportUnmute() {
  const qc = useQueryClient();
  return useMutation({
    // chat_id не передано — тиша знімається в усіх чатах.
    mutationFn: (chatId: number | null) =>
      apiFetch<{ ok: boolean; removed: number }>("/admin/support/unmute", {
        method: "POST",
        body: { chat_id: chatId },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "support"] }),
  });
}

export function useAdminSupportPause() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (paused: boolean) =>
      apiFetch<{ ok: boolean; paused: boolean }>("/admin/support/pause", {
        method: "POST",
        body: { paused },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "support"] }),
  });
}

// --- Акаунт підтримки як користувач MTProto (owner 2026-10-01) -------------
// Вхід в @inflow_manager з адмінки (телефон → код → пароль 2FA) і завантаження
// історії особистих діалогів у журнал підтримки.

export interface SupportImportStats {
  chats_total: number;
  chats_done: number;
  chats_skipped: number;
  messages: number;
  inserted: number;
  existing: number;
  in: number;
  owner: number;
  bot: number;
  flood_waits: number;
}

export interface SupportImportState {
  run_id: string;
  status: "queued" | "running" | "waiting" | "done" | "error";
  since: string | null;
  started_at: string | null;
  updated_at: string | null;
  finished_at: string | null;
  stats: SupportImportStats;
  error: string | null;
  retry_in: number;
}

export interface SupportAccountOut {
  connected: boolean;
  username: string;
  name: string;
  connected_at: string | null;
  storage_ready: boolean;
  creds_ready: boolean;
  pending: {
    stage: "code" | "password";
    phone: string;
    hint: string;
    code_type: string;
    expires_in: number;
  } | null;
  import: SupportImportState | null;
  import_busy: boolean;
}

const SUPPORT_ACCOUNT_KEY = ["admin", "support", "account"] as const;
/** Крок входу ходить у Telegram (нове зʼєднання, ключ) — дефолтного таймауту мало. */
const SUPPORT_LOGIN_TIMEOUT_MS = 60_000;

const importActive = (s: SupportImportState | null | undefined) =>
  !!s && (s.status === "queued" || s.status === "running" || s.status === "waiting");

export function useAdminSupportAccount() {
  return useQuery({
    queryKey: SUPPORT_ACCOUNT_KEY,
    queryFn: () => apiFetch<SupportAccountOut>("/admin/support/account"),
    staleTime: 10_000,
    // Поки йде імпорт — оновлюємо підсумок кожні 5 секунд.
    refetchInterval: (q) => (importActive(q.state.data?.import) ? 5_000 : false),
  });
}

function useSupportAccountMutation<TIn, TOut>(path: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TIn) =>
      apiFetch<TOut>(`/admin/support/account/${path}`, {
        method: "POST",
        body,
        timeoutMs: SUPPORT_LOGIN_TIMEOUT_MS,
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: SUPPORT_ACCOUNT_KEY }),
  });
}

export const useSupportAccountSendCode = () =>
  useSupportAccountMutation<{ phone: string }, { stage: string; phone: string }>("send-code");
export const useSupportAccountSignIn = () =>
  useSupportAccountMutation<{ code: string }, { stage: string; connected?: boolean }>("sign-in");
export const useSupportAccountPassword = () =>
  useSupportAccountMutation<{ password: string }, { stage: string; connected?: boolean }>("password");
export const useSupportAccountCancel = () =>
  useSupportAccountMutation<Record<string, never>, { ok: boolean }>("cancel");
export const useSupportAccountDisconnect = () =>
  useSupportAccountMutation<Record<string, never>, { connected: boolean }>("disconnect");
export const useSupportHistoryImport = () =>
  useSupportAccountMutation<{ since: string | null }, { ok: boolean }>("import");

export function useFinderStats() {
  return useQuery({
    queryKey: ["admin", "finder-stats"],
    queryFn: () => apiFetch<FinderStatsOut>("/admin/finder/stats"),
    staleTime: 60_000,
  });
}

export function useBroadcasts() {
  return useQuery({
    queryKey: ["admin", "broadcasts"],
    queryFn: () => apiFetch<BroadcastListOut>("/admin/broadcasts?limit=20"),
    staleTime: 2_000,
    // Live-прогрес: поки відкрита адмінка — опитуємо кожні 3с, щоб число
    // надісланих росло наживо (owner 2026-07-05). Коли адмінку закрили —
    // компонент розмонтовується → опитування зупиняється.
    refetchInterval: 3_000,
  });
}

/** Керування розсилкою з адмінки (owner 2026-08-27): зупинити, відредагувати
 *  те, що ще не пішло, і продовжити з новим текстом/аудиторією. */
export function useStopBroadcast() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<BroadcastOut>(`/admin/broadcasts/${id}/stop`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "broadcasts"] }),
  });
}

export function useResumeBroadcast() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<BroadcastOut>(`/admin/broadcasts/${id}/resume`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "broadcasts"] }),
  });
}

export function useEditBroadcast() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      id: number;
      message_text?: string;
      segment?: string;
      category_ids?: number[];
    }) => {
      const { id, ...body } = data;
      return apiFetch<BroadcastOut>(`/admin/broadcasts/${id}`, {
        method: "PATCH",
        body,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "broadcasts"] }),
  });
}

export interface CreateBroadcastIn {
  segment: string;
  message_text?: string;
  photo_url?: string | null;
  /** t.me-посилання на пост для пересилання (форвард-режим). */
  forward_link?: string | null;
  /** Обрані сфери — лише для segment="by_category" (owner 2026-07-23). */
  category_ids?: number[];
}

export function useCreateBroadcast() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateBroadcastIn) =>
      apiFetch<BroadcastOut>("/admin/broadcasts", { method: "POST", body: payload }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "broadcasts"] });
    },
  });
}


export interface PodcastOut {
  id: number;
  number: number | null;
  title: string;
  kind: "voice" | "audio" | "video";
  duration_sec: number | null;
  url: string;
}

/**
 * Подкасти засновника (owner 2026-08-09). Посилання на файли підписані й живуть
 * кілька годин, тому staleTime тримаємо коротким — інакше клієнт спробує грати
 * протухле посилання.
 */
export function usePodcasts() {
  return useQuery({
    queryKey: ["podcasts"] as const,
    queryFn: () => apiFetch<PodcastOut[]>("/podcasts"),
    staleTime: 60_000,
  });
}

// Доступ до подкастів. Owner 2026-08-06: «Спільнота» як розділ вимкнена —
// лишився ЛИШЕ цей шлях: із Бази знань у канал із подкастами. Учасник →
// диплінк на подкаст; не-учасник з підпискою → invite; FREE → 403.
export function usePodcastsAccess() {
  return useMutation({
    mutationFn: () =>
      apiFetch<{ url: string }>("/community/podcasts", { method: "POST" }),
  });
}

// AI-генерація відгуку на вакансію (юзер 2026-06-14).
export type AIResponseStyle = "friendly" | "business" | "hook" | "custom";
export interface AIResponseOut {
  id: number;
  vacancy_id: number;
  response_text: string;
  model_used: string | null;
  tokens_used: number | null;
  created_at: string;
}
/** Анонс нових квестів (owner 2026-09-16): вміст живе на сервері, попап у
 *  застосунку його лише малює. Показувати чи ні — вирішуємо за `seen_hints`. */
export function useGenerateAIResponse() {
  return useMutation({
    mutationFn: (vars: {
      vacancy_id: number;
      style?: AIResponseStyle;
      /** Заготовка, за промптом якої генерувати (owner 2026-09-12). */
      preset_id?: number | null;
      /** Мова відгуку (owner 2026-09-16): промт може бути будь-якою мовою,
       *  а відповідь пишемо тією, яку обрала людина. */
      lang?: "uk" | "ru" | "en";
      regenerate?: boolean;
      previous_text?: string | null;
    }) =>
      apiFetch<AIResponseOut>("/ai/generate", {
        method: "POST",
        body: {
          vacancy_id: vars.vacancy_id,
          style: vars.style ?? "friendly",
          preset_id: vars.preset_id ?? null,
          lang: vars.lang ?? "uk",
          regenerate: vars.regenerate ?? false,
          previous_text: vars.previous_text ?? null,
        },
      }),
  });
}

/** Заготовки відгуків: наші три (копіями, тому їх можна правити й видаляти)
 *  + до п'яти своїх (owner 2026-09-12). */
export interface AIPreset {
  id: number;
  name: string;
  prompt: string;
  is_own: boolean;
}

export interface AIPresetsList {
  items: AIPreset[];
  own_left: number;
  own_limit: number;
}

export function useAIPresets() {
  return useQuery({
    queryKey: ["ai", "presets"],
    queryFn: () => apiFetch<AIPresetsList>("/ai/presets"),
    staleTime: 60_000,
  });
}

export function useSaveAIPreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id?: number; name: string; prompt: string }) =>
      apiFetch<AIPresetsList>(
        vars.id ? `/ai/presets/${vars.id}` : "/ai/presets",
        {
          method: vars.id ? "PATCH" : "POST",
          body: { name: vars.name, prompt: vars.prompt },
        },
      ),
    onSuccess: (data) => qc.setQueryData(["ai", "presets"], data),
  });
}

export function useDeleteAIPreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<AIPresetsList>(`/ai/presets/${id}`, { method: "DELETE" }),
    onSuccess: (data) => qc.setQueryData(["ai", "presets"], data),
  });
}

// Голос → структурований опис «Про тебе» (Whisper + AI). Юзер 2026-06-14.
export function useTranscribeProfile() {
  return useMutation({
    mutationFn: (vars: { audio_base64: string; mime: string }) =>
      apiFetch<{ text: string }>("/profile/transcribe", {
        method: "POST",
        body: vars,
        timeoutMs: 120_000,
      }),
  });
}

/** Опитування «як покращити inFLOW» (owner 2026-08-11). */
export interface FeedbackStateOut {
  /** false — збір відгуків закрито (owner 2026-08-28). */
  is_open?: boolean;
  min_chars: number;
  attempts_left: number;
  already_rewarded: boolean;
  /** none — нагороди не було, active — подарунок чекає, used — вже забрав. */
  reward_state?: "none" | "active" | "used" | "closed";
}

export interface FeedbackResultOut {
  verdict: string;
  useful: boolean;
  reason: string;
  rewarded: boolean;
  already_rewarded: boolean;
  attempts_left: number;
}

/** Голос → сирий текст для поля відгуку (owner 2026-08-11). */
export function useTranscribeFeedback() {
  return useMutation({
    mutationFn: (vars: { audio_base64: string; mime: string }) =>
      apiFetch<{ text: string }>("/feedback/transcribe", {
        method: "POST",
        body: vars,
        timeoutMs: 120_000,
      }),
  });
}

export function useFeedbackState() {
  return useQuery({
    queryKey: ["feedback", "state"],
    queryFn: () => apiFetch<FeedbackStateOut>("/feedback/state"),
    staleTime: 0,
  });
}

export function useSubmitFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (text: string) =>
      apiFetch<FeedbackResultOut>("/feedback/submit", {
        method: "POST",
        body: { text },
        // AI читає відгук довше за звичайний запит.
        timeoutMs: 90_000,
      }),
    onSuccess: () => {
      // Подарунок міг щойно відкритись — плашка в підписці має його побачити.
      qc.invalidateQueries({ queryKey: queryKeys.giftWindow });
    },
  });
}


// ============================================================
// Тиждень inFLOW за підписку на Instagram (owner 2026-10-14)
// ============================================================

export interface InstaPromoStateOut {
  /** none · pending · approved · rejected */
  status: string;
  owner_handle: string;
  owner_url: string;
  submitted_url: string | null;
  can_claim: boolean;
  granted: boolean;
  claimed: boolean;
  promo_path: string;
}

export function useInstaPromoState() {
  return useQuery({
    queryKey: ["insta-promo", "state"],
    queryFn: () => apiFetch<InstaPromoStateOut>("/insta-promo/state"),
    staleTime: 0,
  });
}

export function useSubmitInstaPromo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (url: string) =>
      apiFetch<InstaPromoStateOut>("/insta-promo/submit", {
        method: "POST",
        body: { url },
      }),
    onSuccess: (data) => {
      qc.setQueryData(["insta-promo", "state"], data);
    },
  });
}


// ============================================================
// MCP від inFLOW — підключення до ChatGPT / Claude / Cursor (owner 2026-10-30)
// ============================================================

export interface McpConnection {
  enabled: boolean;
  url: string | null;
  created_at: string | null;
  last_used_at: string | null;
  last_client: string | null;
  calls_total: number;
  /** Сторож вимкнув підключення — до цього часу знову не ввімкнути. */
  blocked_until: string | null;
}

export function useMcpConnection() {
  return useQuery({
    queryKey: ["mcp", "connection"] as const,
    queryFn: () => apiFetch<McpConnection>("/mcp/connection"),
    staleTime: 30_000,
  });
}

export function useChangeMcpConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (action: "enable" | "reissue" | "disable") =>
      apiFetch<McpConnection>("/mcp/connection", { method: "POST", body: { action } }),
    onSuccess: (data) => qc.setQueryData(["mcp", "connection"], data),
  });
}
